import moment from 'moment-timezone';

// OT availability sign-on windows. Spec:
// docs/superpowers/specs/2026-10-07-ot-availability-sign-on-windows-design.md
// Stored and sent values are UTC times of day. Everything the user sees is local (moment's
// default zone, which is the tenancy zone). Only toUtcValue/toLocalValue convert.
// Keep this module free of AuthService: core/helpers.jsx imports it.

export const ANY = 'Any';
export const MAX_WINDOWS = 4;
export const DEFAULT_PRESETS = { ds: '03:00-14:00', ns: '15:00-02:00' };
export const TASK = 'availablesignon';
export const SIGN_ON_TOOLTIP = 'Only activities that allow sign-on windows.';

export const MESSAGES = {
  V2: 'Every window needs both a start and an end time.',
  V3_HOURS: 'Hours must be 00–23 (24-hour clock).',
  V3_MINUTES: 'Minutes must be 00–59.',
  V4: 'Start and end cannot be the same time.',
  V5: 'Windows overlap. Merge them into one range.',
  V6: '"Any" cannot be combined with time windows.',
  V7: 'Remove the trailing ";".',
  V8: 'Up to 4 windows per assignment.',
  STEP: 'Use :00 or :30 for the minutes.'
};

const MINUTES_PER_DAY = 1440;
const WINDOW_RE = /^(\d{1,2}):(\d{1,2})\s*-\s*(\d{1,2}):(\d{1,2})$/;

const pad = n => String(n).padStart(2, '0');
const fmt = minutes => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
const fail = rule => ({ value: null, error: { rule, message: MESSAGES[rule] } });
const splitWindows = value => value.split(';').map(window => window.split('-'));

/**
 * @description Normalise and validate a value with the same rules as the backend parser.
 * @param {string|null} raw - the value to check
 * @param {object} options - requireHalfHour adds the UI-only :00/:30 rule
 * @return {{value: (string|null), error: (object|null)}}
 */
export const parseAvailableSignOn = (raw, { requireHalfHour = false } = {}) => {
  const text = (raw || '').trim();
  if (!text) {
    return { value: null, error: null };
  }
  if (text.toLowerCase() === 'any') {
    return { value: ANY, error: null };
  }
  if (text.endsWith(';')) {
    return fail('V7');
  }
  const windows = [];
  const parts = text.split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i].trim();
    if (!part) {
      return fail('V7');
    }
    if (part.toLowerCase() === 'any') {
      return fail('V6');
    }
    const match = WINDOW_RE.exec(part);
    if (!match) {
      return fail('V2');
    }
    const [h1, m1, h2, m2] = match.slice(1).map(Number);
    if (h1 > 23 || h2 > 23) {
      return fail('V3_HOURS');
    }
    if (m1 > 59 || m2 > 59) {
      return fail('V3_MINUTES');
    }
    if (requireHalfHour && [m1, m2].some(m => m % 30 !== 0)) {
      return fail('STEP');
    }
    const start = (h1 * 60) + m1;
    const end = (h2 * 60) + m2;
    if (start === end) {
      return fail('V4');
    }
    windows.push([start, end]);
  }
  if (windows.length > MAX_WINDOWS) {
    return fail('V8');
  }
  const seen = new Set();
  for (let i = 0; i < windows.length; i += 1) {
    const [start, end] = windows[i];
    for (let minute = start; minute !== end; minute = (minute + 1) % MINUTES_PER_DAY) {
      if (seen.has(minute)) {
        return fail('V5');
      }
      seen.add(minute);
    }
  }
  windows.sort((a, b) => a[0] - b[0]);
  return { value: windows.map(([start, end]) => `${fmt(start)}-${fmt(end)}`).join(';'), error: null };
};

export const isOta = activity => !!activity && activity.allows_sign_on_windows === true;

export const formatAvailableSignOn = (localValue) => {
  if (!localValue) {
    return 'Not set';
  }
  if (localValue === ANY) {
    return 'Any sign-on time';
  }
  return splitWindows(localValue).map(([start, end]) => `${start}–${end}`).join(', ');
};

const displayZone = () => (moment.defaultZone ? moment.defaultZone.name : moment.tz.guess());

const atTime = (base, hhmm) => {
  const [hour, minute] = hhmm.split(':').map(Number);
  return base.clone().set({
    hour, minute, second: 0, millisecond: 0
  });
};

// Anchor rule: the first instant at or after `from` whose time (in `from`'s zone) is hhmm.
const firstAtOrAfter = (from, hhmm) => {
  const candidate = atTime(from, hhmm);
  return candidate.isBefore(from) ? candidate.add(1, 'day') : candidate;
};

const placeWindows = (value, anchor) => splitWindows(value).map(([start, end]) => {
  const startAt = firstAtOrAfter(anchor, start);
  return [startAt, firstAtOrAfter(startAt.clone().add(1, 'minute'), end)];
});

/**
 * @description Local value (display zone) -> stored UTC value, for one assignment.
 * Call only with a valid value; null and Any pass through.
 */
export const toUtcValue = (localValue, assignmentStart) => {
  if (!localValue || localValue === ANY) {
    return localValue || null;
  }
  const anchor = moment(assignmentStart).tz(displayZone());
  const utc = placeWindows(localValue, anchor)
    .map(([start, end]) => `${start.clone().utc().format('HH:mm')}-${end.clone().utc().format('HH:mm')}`)
    .join(';');
  const { value, error } = parseAvailableSignOn(utc);
  // If the converted UTC string fails validation (e.g. DST fall-back creates overlapping windows),
  // return it as-is so the server can reject it with a message rather than clearing the value.
  return error ? utc : value;
};

/**
 * @description Stored UTC value -> local value (display zone), in start order.
 * An invalid stored value is returned unchanged so the field can show it.
 */
export const toLocalValue = (utcValue, assignmentStart) => {
  if (!utcValue || utcValue === ANY || parseAvailableSignOn(utcValue).error) {
    return utcValue || null;
  }
  const zone = displayZone();
  return placeWindows(utcValue, moment.utc(assignmentStart))
    .sort((a, b) => a[0].valueOf() - b[0].valueOf())
    .map(([start, end]) => `${start.clone().tz(zone).format('HH:mm')}-${end.clone().tz(zone).format('HH:mm')}`)
    .join(';');
};

const toRows = value => splitWindows(value).map(([start = '', end = '']) => ({ start, end }));

export const toUiState = (localValue, presets) => {
  if (!localValue) {
    return { mode: null, rows: [], error: null };
  }
  const { value, error } = parseAvailableSignOn(localValue);
  if (error) {
    return { mode: 'custom', rows: toRows(localValue), error: { ...error, message: `${error.message} Saved value: ${localValue}` } };
  }
  if (value === ANY) {
    return { mode: 'any', rows: [], error: null };
  }
  if (value === parseAvailableSignOn(presets.ds).value) {
    return { mode: 'ds', rows: [], error: null };
  }
  if (value === parseAvailableSignOn(presets.ns).value) {
    return { mode: 'ns', rows: [], error: null };
  }
  return { mode: 'custom', rows: toRows(value), error: null };
};

export const applySignOn = (items, localValue) => items.map(item => ({
  ...item,
  available_sign_on: toUtcValue(localValue, item.start_time)
}));

const personName = ({ user }) => (user && user.first_name
  ? `${user.last_name.toUpperCase()}, ${user.first_name}`
  : 'UNALLOCATED');

export const signOnToast = (items, localValue) => {
  const shown = formatAvailableSignOn(localValue);
  if (items.length > 1) {
    return localValue
      ? `OT availability set to ${shown} on ${items.length} assignments`
      : `OT availability cleared on ${items.length} assignments`;
  }
  return localValue
    ? `OT availability set to ${shown} for ${personName(items[0])}`
    : `OT availability cleared for ${personName(items[0])}`;
};

export const signOnMenuState = (clicked, selectedItems = []) => {
  if (!clicked || !isOta(clicked.activity)) {
    return {
      visible: false, title: '', disabled: true, tooltip: null
    };
  }
  if (selectedItems.length > 1) {
    const allOta = selectedItems.every(i => isOta(i.activity));
    return {
      visible: true,
      title: `Set OT availability for ${selectedItems.length}…`,
      disabled: !allOta,
      tooltip: allOta ? null : SIGN_ON_TOOLTIP
    };
  }
  return {
    visible: true,
    title: clicked.available_sign_on ? 'Edit OT availability…' : 'Set OT availability…',
    disabled: false,
    tooltip: null
  };
};

const normaliseLocal = (v) => {
  if (!v) {
    return v;
  }
  const { value, error } = parseAvailableSignOn(v);
  return error ? v : value;
};

export const sharedLocalValue = (items) => {
  const values = items.map(i => normaliseLocal(toLocalValue(i.available_sign_on || null, i.start_time)));
  return values.every(v => v === values[0])
    ? { mixed: false, value: values[0] }
    : { mixed: true, value: null };
};
